import type { CardDto, ColumnDto } from '@kanban/shared';
import { DndContext } from '@dnd-kit/core';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { Avatar } from '../components/Avatar.js';
import { CardSheet } from '../components/CardSheet.js';
import { ColumnSettings } from '../components/ColumnSettings.js';
import { ColumnView } from '../components/ColumnView.js';
import { FilterBar } from '../components/FilterBar.js';
import { QuickCaptureSheet } from '../components/QuickCaptureSheet.js';
import { ArchiveSheet } from '../components/ArchiveSheet.js';
import { BoardSettingsSheet } from '../components/BoardSettingsSheet.js';
import { CardDragOverlay } from '../components/dnd/CardDragOverlay.js';
import { TrashZone } from '../components/dnd/TrashZone.js';
import { useBoardDnd } from '../components/dnd/useBoardDnd.js';
import { positionForDrop } from '../lib/drag-position.js';
import {
  useArchiveCard,
  useBoard,
  useCreateCard,
  useCreateColumn,
  useMoveCard,
  usePatchCard,
} from '../lib/boards.js';
import {
  filterFromParams,
  filterToParams,
  matchesFilter,
  type CardFilter,
} from '../lib/card-filter.js';
import { useRealtime } from '../lib/realtime.js';
import { playNudgeSound } from '../lib/sound.js';
import { useCurrentUser } from '../lib/session.js';
import { useToast } from '../lib/toast.js';

/** Randzone fuer das Blaettern zur Nachbarspalte (SPEC 8.2.6). */
const EDGE_ZONE_PX = 48;
const EDGE_DWELL_MS = 500;

/**
 * Board-Ansicht, Mobile First auf 375 px entworfen (SPEC 8.1): Spalten
 * horizontal wischbar mit Scroll-Snap, ab `md:` nebeneinander.
 */
export function BoardPage() {
  const { boardId = '' } = useParams();
  const me = useCurrentUser();
  const board = useBoard(boardId);
  const [searchParams, setSearchParams] = useSearchParams();
  const createCard = useCreateCard(boardId);
  const createColumn = useCreateColumn(boardId);
  const moveCard = useMoveCard(boardId);
  const archiveCard = useArchiveCard(boardId);

  // Karte und Filter stehen in der URL (SPEC 9.3). Der Filter ist damit
  // teilbar, und ein Klick auf eine Push-Benachrichtigung oeffnet direkt die
  // Karte (SPEC 10.1).
  const openCardId = searchParams.get('card');
  const filter = useMemo(() => filterFromParams(searchParams), [searchParams]);

  const setFilter = useCallback(
    (next: CardFilter) => {
      const params = filterToParams(next);
      const card = searchParams.get('card');
      if (card) params.set('card', card);
      setSearchParams(params, { replace: true });
    },
    [searchParams, setSearchParams],
  );

  const openCard = useCallback(
    (cardId: string | null) => {
      const params = new URLSearchParams(searchParams);
      if (cardId) params.set('card', cardId);
      else params.delete('card');
      setSearchParams(params, { replace: true });
    },
    [searchParams, setSearchParams],
  );

  const [settingsColumn, setSettingsColumn] = useState<ColumnDto | null>(null);
  const [archiveOpen, setArchiveOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [quickCapture, setQuickCapture] = useState(false);

  /*
   * Aktionen aus einer Benachrichtigung (SPEC 10.1).
   *
   * Der Service Worker haengt `aktion` an die URL, statt selbst zu schreiben —
   * er kommt an das CSRF-Cookie nicht heran. Ausgefuehrt wird hier, mit der
   * normalen Sitzung, und der Parameter verschwindet danach sofort aus der
   * URL: sonst wuerde ein Reload oder ein geteilter Link die Karte ein
   * zweites Mal abhaken.
   */
  const aktion = searchParams.get('aktion');
  const patchCard = usePatchCard(boardId);
  const handledAction = useRef<string | null>(null);

  useEffect(() => {
    if (!aktion || !openCardId) return;

    /*
     * Erst die Karte, dann die Merkmarke — in der anderen Reihenfolge waere
     * die Aktion als erledigt vermerkt, bevor das Board ueberhaupt geladen
     * ist, und wuerde danach nie ausgefuehrt.
     */
    const card = board.data?.columns
      .flatMap((column) => column.cards)
      .find((candidate) => candidate.id === openCardId);
    if (!card) return;

    const marker = `${openCardId}:${aktion}`;
    if (handledAction.current === marker) return;
    handledAction.current = marker;

    if (aktion === 'erledigt') {
      patchCard.mutate({ cardId: card.id, patch: { completed: true }, updatedAt: card.updatedAt });
    } else if (aktion === 'snooze') {
      const base = card.dueAt ? new Date(card.dueAt) : new Date();
      patchCard.mutate({
        cardId: card.id,
        patch: { dueAt: new Date(base.getTime() + 3_600_000).toISOString() },
        updatedAt: card.updatedAt,
      });
    }

    const params = new URLSearchParams(searchParams);
    params.delete('aktion');
    setSearchParams(params, { replace: true });
  }, [aktion, openCardId, board.data, patchCard, searchParams, setSearchParams]);
  const scroller = useRef<HTMLDivElement>(null);
  const [visibleColumnIndex, setVisibleColumnIndex] = useState(0);

  const toast = useToast();

  /*
   * Eine Anmahnung soll auffallen — sie ist der einzige Fall, in dem ein
   * anderes Familienmitglied ausdruecklich die Aufmerksamkeit will. Deshalb
   * Einblendung, Vibration und Ton zusammen. Alle anderen Ereignisse
   * schreiben still in den Cache; Toasts fuer jede fremde Bewegung waeren
   * Laerm.
   */
  const onRemoteChange = useCallback(
    (event: { type: string; actorName: string; payload: unknown }) => {
      if (event.type !== 'card.nudged') return;

      const { title } = (event.payload ?? {}) as { title?: string };
      toast(
        title
          ? `${event.actorName} erinnert dich: „${title}" ist überfällig`
          : `${event.actorName} erinnert dich an eine überfällige Aufgabe`,
        'error',
      );
      navigator.vibrate?.([80, 60, 80]);
      playNudgeSound();
    },
    [toast],
  );

  const { connection, presence, reportFocus } = useRealtime(boardId, onRemoteChange);

  /*
   * Was gerade offen ist, dem Board melden (SPEC 7.3). Haengt an `openCardId`
   * und damit an der URL — also an genau einer Stelle, statt an jedem Auf-
   * und Zuklappen einzeln.
   */
  useEffect(() => {
    reportFocus(openCardId);
    return () => reportFocus(null);
  }, [openCardId, reportFocus]);


  const columns = board.data?.columns ?? [];

  const dnd = useBoardDnd({
    columns,
    onMove: useCallback(
      ({ cardId, target, position }) =>
        moveCard.mutate({ cardId, columnId: target.columnId, target, position }),
      [moveCard],
    ),
    onTrash: useCallback((cardId: string) => archiveCard.mutate(cardId), [archiveCard]),
  });

  // Welche Spalte ist sichtbar? Der FAB legt die Karte dort an (SPEC 8.1).
  useEffect(() => {
    const element = scroller.current;
    if (!element) return;
    const onScroll = () => {
      const width = element.clientWidth * 0.88;
      setVisibleColumnIndex(Math.round(element.scrollLeft / Math.max(width, 1)));
    };
    element.addEventListener('scroll', onScroll, { passive: true });
    return () => element.removeEventListener('scroll', onScroll);
  }, [columns.length]);

  /**
   * Spaltenwechsel am Bildschirmrand (SPEC 8.2.6): verweilt der Finger beim
   * Ziehen 500 ms in der Randzone, wird zur Nachbarspalte geblaettert.
   * dnd-kit scrollt zwar selbst, aber nur pixelweise — bei Scroll-Snap-Spalten
   * ist das Springen zur Nachbarspalte das, was man erwartet.
   */
  useEffect(() => {
    if (!dnd.activeCard) return;
    const element = scroller.current;
    if (!element) return;

    let timer: ReturnType<typeof setInterval> | null = null;
    let zone: 'links' | 'rechts' | null = null;

    const clear = () => {
      if (timer) clearInterval(timer);
      timer = null;
      zone = null;
    };

    const onPointerMove = (event: PointerEvent) => {
      const rect = element.getBoundingClientRect();
      const next: 'links' | 'rechts' | null =
        event.clientX < rect.left + EDGE_ZONE_PX
          ? 'links'
          : event.clientX > rect.right - EDGE_ZONE_PX
            ? 'rechts'
            : null;

      if (next === zone) return;
      clear();
      zone = next;
      if (!next) return;

      // Intervall statt einmaligem Timer: wer am Rand haelt, blaettert weiter.
      // Mit einem Timeout muesste man den Finger jedes Mal herausziehen und
      // wieder hineinbewegen, um zur uebernaechsten Spalte zu kommen.
      timer = setInterval(() => {
        const step = element.clientWidth * 0.88;
        element.scrollBy({ left: next === 'links' ? -step : step, behavior: 'smooth' });
      }, EDGE_DWELL_MS);
    };

    window.addEventListener('pointermove', onPointerMove);
    return () => {
      window.removeEventListener('pointermove', onPointerMove);
      clear();
    };
  }, [dnd.activeCard]);

  if (board.isLoading) return <p className="p-4 text-sm text-muted">Lädt …</p>;
  if (board.isError || !board.data) {
    return (
      <div className="p-4">
        <p role="alert" className="text-sm text-danger">
          Board konnte nicht geladen werden.
        </p>
        <Link to="/" className="mt-2 inline-block text-sm text-accent">
          Zurück zur Übersicht
        </Link>
      </div>
    );
  }

  const data = board.data;

  /*
   * Anwesende zu Personen aufloesen. Wer nicht (mehr) Mitglied des Boards
   * ist, faellt heraus — ein Avatar ohne Namen waere nur ein Raetsel.
   */
  const anwesend = presence
    .filter((entry) => entry.userId !== me.id)
    .map((entry) => data.members.find((member) => member.id === entry.userId))
    .filter((member): member is NonNullable<typeof member> => member !== undefined);

  /** Karte -> wer sie gerade offen hat. Der Name steht im Titel des Rahmens. */
  const beobachtet = new Map(
    presence
      .filter((entry) => entry.userId !== me.id && entry.cardId !== null)
      .map((entry) => [
        entry.cardId as string,
        data.members.find((member) => member.id === entry.userId)?.name ?? 'Jemand',
      ]),
  );

  const activeCard: CardDto | undefined = data.columns
    .flatMap((column) => column.cards)
    .find((card) => card.id === openCardId);

  /**
   * Erledigt-Knopf auf der Kachel (SPEC-Ergaenzung): derselbe Endpunkt wie
   * beim Ziehen in die Erledigt-Spalte, nur ohne das Kartendetail zu oeffnen.
   */
  function completeCard(card: CardDto) {
    const done = data.columns.find((column) => column.isDoneColumn);
    if (!done || card.columnId === done.id) return;
    const others = done.cards.filter((c) => c.id !== card.id);
    const target = { columnId: done.id, index: others.length };
    moveCard.mutate({
      cardId: card.id,
      columnId: done.id,
      target,
      position: positionForDrop(data.columns, card.id, target),
    });
  }

  // Gefiltert wird nur die Anzeige. Verschieben und Zaehler arbeiten weiter
  // auf dem vollstaendigen Stand — sonst wuerde eine ausgeblendete Karte beim
  // Ablegen an der falschen Stelle landen.
  const visibleColumns = data.columns.map((column) => ({
    ...column,
    cards: column.cards.filter((card) => matchesFilter(card, filter, me.id)),
  }));
  const matchCount = visibleColumns.reduce((sum, column) => sum + column.cards.length, 0);

  const targetColumn = data.columns[Math.min(visibleColumnIndex, data.columns.length - 1)];

  return (
    <div className="flex h-full flex-col">
      <header className="flex items-center gap-3 px-4 py-3">
        <Link to="/" aria-label="Zurück zur Board-Übersicht" className="text-muted">
          ←
        </Link>
        {/*
          Der Board-Name fuehrt zu den Einstellungen. Mit sichtbarem Zahnrad —
          eine Ueberschrift, die heimlich klickbar ist, findet niemand.
          Als `<h1>` mit einem Knopf darin: die Gliederung der Seite soll die
          Ueberschrift behalten.
        */}
        <h1 className="min-w-0 truncate text-lg font-semibold">
          <button
            type="button"
            data-testid="open-board-settings"
            onClick={() => setSettingsOpen(true)}
            title="Board-Einstellungen"
            className="flex max-w-full items-center gap-1.5"
          >
            <span className="truncate">{data.title}</span>
            <span aria-hidden="true" className="shrink-0 text-xs text-muted">
              ⚙
            </span>
          </button>
        </h1>

        {/*
          Wer sonst noch da ist (SPEC 7.3). Nur die anderen — sich selbst
          sieht man ohnehin, und auf 375 px ist jeder Pixel vergeben.
        */}
        {anwesend.length > 0 && (
          <span className="flex -space-x-1" data-testid="presence">
            {anwesend.map((person) => (
              <Avatar key={person.id} name={person.name} color={person.avatarColor} />
            ))}
          </span>
        )}
        {/* Nur melden, wenn etwas fehlt — ein Dauer-Grün lenkt nur ab. */}
        {connection === 'getrennt' && (
          <span
            data-testid="connection-state"
            className="ml-auto rounded-full bg-warning-soft px-2 py-0.5 text-xs text-warning"
          >
            offline
          </span>
        )}
        <button
          type="button"
          data-testid="open-archive"
          onClick={() => setArchiveOpen(true)}
          aria-label="Archiv öffnen"
          title="Archiv"
          className={`${connection === 'getrennt' ? '' : 'ml-auto'} shrink-0 text-muted`}
          style={{ minWidth: 'var(--touch-target)', minHeight: 'var(--touch-target)' }}
        >
          <span aria-hidden="true">🗄</span>
        </button>
      </header>

      <FilterBar
        board={data}
        me={me}
        filter={filter}
        onChange={setFilter}
        matchCount={matchCount}
      />

      <DndContext {...dnd.dndContextProps}>
        <div
          ref={scroller}
          className="board-scroller flex min-h-0 flex-1 gap-3 overflow-x-auto px-4 pb-4"
        >
          {visibleColumns.map((column) => (
            <ColumnView
              key={column.id}
              column={column}
              onOpenCard={(card) => openCard(card.id)}
              onAddCard={(columnId, title) => createCard.mutate({ columnId, title })}
              onOpenSettings={() => setSettingsColumn(column)}
              activeCardId={dnd.activeCard?.id ?? null}
              watchers={beobachtet}
              dropIndex={dnd.dropTarget?.columnId === column.id ? dnd.dropTarget.index : null}
              dropHeight={dnd.cardHeight}
              onCompleteCard={completeCard}
            />
          ))}

          <div className="board-column flex items-start pt-2">
            <button
              type="button"
              onClick={() => {
                const title = window.prompt('Titel der neuen Spalte');
                if (title?.trim()) createColumn.mutate(title.trim());
              }}
              className="w-full rounded-[var(--radius-lg)] border border-dashed border-border-strong px-4 text-sm text-muted"
              style={{ minHeight: 'var(--touch-target)' }}
            >
              + Spalte
            </button>
          </div>
        </div>

        <TrashZone visible={dnd.showTrash} active={dnd.overTrash} />
        <CardDragOverlay card={dnd.activeCard} />
      </DndContext>

      {/* Floating Action Button: Karte in der sichtbaren Spalte (SPEC 8.1). */}
      {targetColumn && !dnd.activeCard && (
        <button
          type="button"
          data-testid="fab-add-card"
          onClick={() => setQuickCapture(true)}
          className="fixed bottom-[calc(var(--bottom-nav-height)+1rem)] right-4 z-20 flex items-center justify-center rounded-full bg-accent text-2xl text-[var(--color-accent-contrast)] shadow-[var(--shadow-3)] md:hidden"
          style={{ height: '3.5rem', width: '3.5rem' }}
          aria-label={`Karte in Spalte ${targetColumn.title} hinzufügen`}
        >
          +
        </button>
      )}

      {activeCard && <CardSheet board={data} card={activeCard} onClose={() => openCard(null)} />}

      {settingsColumn && (
        <ColumnSettings
          boardId={boardId}
          column={settingsColumn}
          onClose={() => setSettingsColumn(null)}
        />
      )}

      {archiveOpen && <ArchiveSheet boardId={boardId} onClose={() => setArchiveOpen(false)} />}
      {settingsOpen && (
        <BoardSettingsSheet board={data} onClose={() => setSettingsOpen(false)} />
      )}

      {quickCapture && targetColumn && (
        <QuickCaptureSheet
          boardId={boardId}
          column={targetColumn}
          onClose={() => setQuickCapture(false)}
        />
      )}
    </div>
  );
}
